import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AsyncButton } from '@/components/common/AsyncButton';
import { FilterBar } from '@/components/common/FilterBar';
import { DataGrid, type DataGridColumn } from '@/components/tables/DataGrid';
import { Select2 } from '@/components/forms/Select2';
import type { Select2Option } from '@/components/forms/Select2';
import { useToast } from '@/components/common/toast';
import { ApiError } from '@/api/client/abpError';
import type {
  ProviderPeriodLockLogDto,
  ProviderPeriodStatusDto,
  ResultDto,
} from '@/api/types/providerFunds';
import type { ProviderFundsConfig } from './config';
import { monthKey, renderNumber, renderPlDateTime, renderPlMonth } from './renderers';
import { providerPeriodLockingApi } from '@/api/services/providerFunds';
import { CommentConfirmDialog } from './CommentConfirmDialog';

interface PeriodLockingTabProps {
  config: ProviderFundsConfig;
  canViewRealCashBalance: boolean;
  canLock: boolean;
  canUnlock: boolean;
  selectedProviderId: string | null;
}

type StatusFilter = 'Locked' | 'Processing' | 'Unlocked' | null;
type ConfirmState =
  | { kind: 'lock' | 'unlock'; title: string; body: string; confirmLabel: string; year: number; month: number }
  | null;

export function PeriodLockingTab({
  canViewRealCashBalance,
  canLock,
  canUnlock,
  selectedProviderId,
}: PeriodLockingTabProps) {
  const toast = useToast();

  const [providerId, setProviderId] = useState<string | null>(selectedProviderId);
  const [selectedMonthKey, setSelectedMonthKey] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(null);
  const [showAudit, setShowAudit] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmState>(null);
  const [acting, setActing] = useState(false);

  useEffect(() => {
    if (selectedProviderId) setProviderId(selectedProviderId);
  }, [selectedProviderId]);

  const providersQuery = useQuery({
    queryKey: ['pa', 'lock-providers'],
    queryFn: providerPeriodLockingApi.getProviders,
    staleTime: Infinity,
  });
  const providerOptions: Select2Option[] = useMemo(
    () => (providersQuery.data ?? []).map((p: ResultDto) => ({ value: p.value, label: p.text })),
    [providersQuery.data],
  );

  const status = useQuery({
    queryKey: ['pa', 'period-status', providerId],
    queryFn: () => providerPeriodLockingApi.getPeriodStatus(providerId!),
    enabled: Boolean(providerId),
  });
  const audit = useQuery({
    queryKey: ['pa', 'period-audit', providerId],
    queryFn: () => providerPeriodLockingApi.getAuditHistory(providerId!),
    enabled: Boolean(providerId) && showAudit,
  });

  const months = status.data?.months ?? [];

  // Default month = last lockable (!isLocked && isProcessed), else the last month.
  useEffect(() => {
    if (!months.length) {
      setSelectedMonthKey(null);
      return;
    }
    const lockable = [...months].reverse().find((m) => !m.isLocked && m.isProcessed);
    const chosen = lockable ?? months[months.length - 1];
    setSelectedMonthKey(monthKey(chosen.periodMonth));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.data]);

  const monthOptions: Select2Option[] = useMemo(
    () => months.map((m) => ({ value: monthKey(m.periodMonth), label: renderPlMonth(m.periodMonth) })),
    [months],
  );

  const selectedMonth = useMemo(
    () => months.find((m) => monthKey(m.periodMonth) === selectedMonthKey) ?? null,
    [months, selectedMonthKey],
  );

  const filteredMonths = useMemo(
    () => (statusFilter ? months.filter((m) => m.status === statusFilter) : months),
    [months, statusFilter],
  );

  function statusBadge(s: string) {
    const cls = s === 'Locked' ? 'bg-primary' : s === 'Processing' ? 'bg-warning text-dark' : 'bg-danger';
    return <span className={`badge ${cls}`}>{s}</span>;
  }

  const statusColumns: DataGridColumn<ProviderPeriodStatusDto>[] = useMemo(() => {
    const cols: DataGridColumn<ProviderPeriodStatusDto>[] = [
      { key: 'periodMonth', title: 'Period', value: (r) => renderPlMonth(r.periodMonth), cell: (r) => renderPlMonth(r.periodMonth) },
      { key: 'status', title: 'Status', value: (r) => r.status, cell: (r) => statusBadge(r.status) },
      { key: 'cashBookBalanceRc', title: 'Cash Book (RC)', className: 'text-end', value: (r) => r.cashBookBalanceRc, cell: (r) => renderNumber(r.cashBookBalanceRc) },
    ];
    if (canViewRealCashBalance) {
      cols.push({ key: 'realCashBalanceRc', title: 'Real Cash (RC)', className: 'text-end', value: (r) => r.realCashBalanceRc, cell: (r) => renderNumber(r.realCashBalanceRc) });
    }
    return cols;
  }, [canViewRealCashBalance]);

  const auditColumns: DataGridColumn<ProviderPeriodLockLogDto>[] = [
    { key: 'periodMonth', title: 'Period', value: (r) => renderPlMonth(r.periodMonth), cell: (r) => renderPlMonth(r.periodMonth) },
    {
      key: 'action',
      title: 'Action',
      value: (r) => r.action,
      cell: (r) => <span className={`badge ${r.action === 'LOCK' ? 'bg-primary-subtle text-primary' : 'bg-warning-subtle text-warning'}`}>{r.action}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      value: (r) => r.status,
      cell: (r) => <span className={`badge ${r.status === 'success' ? 'bg-primary' : 'bg-danger'}`}>{r.status}</span>,
    },
    { key: 'user', title: 'Created By', value: (r) => r.user },
    { key: 'createdAt', title: 'Created At', value: (r) => renderPlDateTime(r.createdAt), cell: (r) => renderPlDateTime(r.createdAt) },
    { key: 'comment', title: 'Comment', value: (r) => r.comment },
  ];

  function monthLabel(m: ProviderPeriodStatusDto) {
    return renderPlMonth(m.periodMonth);
  }

  function beginLock() {
    if (!selectedMonth) return;
    const label = monthLabel(selectedMonth);
    if (selectedMonth.isLocked) {
      toast.info(`${label} is already locked. No action needed.`);
      return;
    }
    if (!selectedMonth.isProcessed) {
      toast.info(`Balance processing for ${label} is not completed. Please complete EOD / manual processing before locking this period.`);
      return;
    }
    // Cascade = all unlocked months up to and including the target.
    const targetIdx = months.findIndex((m) => monthKey(m.periodMonth) === selectedMonthKey);
    const affected = months.filter((m, i) => i <= targetIdx && !m.isLocked);
    const range = affected.length ? `${monthLabel(affected[0])} – ${monthLabel(affected[affected.length - 1])}` : label;
    // Guard: warn when earlier periods are still unlocked, since locking this month
    // also locks every earlier unlocked period (periods lock in chronological order).
    const earlierUnlocked = months.filter((m, i) => i < targetIdx && !m.isLocked);
    const warning = earlierUnlocked.length
      ? `Note: ${earlierUnlocked.length} earlier period(s) are not locked yet and will be locked as well — periods must be locked in chronological order. `
      : '';
    setConfirm({
      kind: 'lock',
      title: 'Confirm Lock',
      body: `${warning}The following periods will be locked: ${range}. Once locked, all transactions in these periods will be frozen and excluded from further modifications (add / edit / import). Do you want to continue?`,
      confirmLabel: 'Lock',
      year: selectedMonth.year,
      month: selectedMonth.month,
    });
  }

  function beginUnlock() {
    if (!selectedMonth) return;
    const label = monthLabel(selectedMonth);
    if (!selectedMonth.isLocked) {
      toast.info(`${label} is not currently locked.`);
      return;
    }
    // Affected = this month + all subsequent locked months up to the watermark.
    const targetIdx = months.findIndex((m) => monthKey(m.periodMonth) === selectedMonthKey);
    const affected = months.filter((m, i) => i >= targetIdx && m.isLocked);
    const range = affected.length ? `${monthLabel(affected[0])} – ${monthLabel(affected[affected.length - 1])}` : label;
    setConfirm({
      kind: 'unlock',
      title: 'Confirm Unlock',
      body: `Unlocking ${label} will also unlock all subsequent locked months: ${range}. This may affect balance calculations across ${affected.length} period(s). Do you want to continue?`,
      confirmLabel: 'Unlock All',
      year: selectedMonth.year,
      month: selectedMonth.month,
    });
  }

  async function runConfirm(comment: string) {
    if (!confirm || !providerId) return;
    setActing(true);
    try {
      const input = { providerId, year: confirm.year, month: confirm.month, comment };
      const res = confirm.kind === 'lock'
        ? await providerPeriodLockingApi.lock(input)
        : await providerPeriodLockingApi.unlock(input);
      if (res.isSuccess) {
        toast.success(res.message);
        status.refetch();
        if (showAudit) audit.refetch();
      } else {
        toast.error(res.message);
      }
      setConfirm(null);
    } catch (err) {
      setConfirm(null);
      toast.error(err instanceof ApiError ? err.message : 'The operation failed.');
    } finally {
      setActing(false);
    }
  }

  const legend: { label: StatusFilter; cls: string }[] = [
    { label: 'Locked', cls: 'bg-primary' },
    { label: 'Processing', cls: 'bg-warning text-dark' },
    { label: 'Unlocked', cls: 'bg-danger' },
  ];

  return (
    <div>
      <FilterBar
        fields={[
          {
            node: <Select2 label="Provider" placeholder="Select a provider" value={providerId} options={providerOptions} onChange={setProviderId} />,
            width: 200,
          },
          {
            node: <Select2 label="Select Month" value={selectedMonthKey} options={monthOptions} allowClear={false} searchable={false} onChange={setSelectedMonthKey} />,
            width: 180,
          },
        ]}
        actions={
          <>
            {canLock && (
              <AsyncButton className="btn btn-primary btn-sm" icon="fa fa-lock" onClick={beginLock} disabled={!selectedMonth}>
                Lock
              </AsyncButton>
            )}
            {canUnlock && (
              <AsyncButton className="btn btn-outline-primary btn-sm" icon="fa fa-unlock-alt" onClick={beginUnlock} disabled={!selectedMonth}>
                Unlock
              </AsyncButton>
            )}
          </>
        }
      />

      {/* Period Status card renders even before a provider is picked — the grid
          stays visible and empty, filling in once a provider is selected. */}
      <>
          <div className="card custom-card mb-3">
            <div className="card-header d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h6 className="card-title mb-0">{status.data?.providerName ? `${status.data.providerName} — Period Status` : 'Period Status'}</h6>
              <div className="d-flex align-items-center gap-2">
                {legend.map((l) => (
                  <button
                    key={l.label ?? ''}
                    type="button"
                    className={`badge ${l.cls} border-0${statusFilter === l.label ? ' opacity-100' : ' opacity-75'}`}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setStatusFilter((f) => (f === l.label ? null : l.label))}
                    title="Filter by status"
                  >
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="card-body p-0">
              <DataGrid
                columns={statusColumns}
                data={filteredMonths}
                rowKey={(r) => monthKey(r.periodMonth)}
                columnVisibilityToggle={false}
                toolbarStart={
                  <button
                    type="button"
                    className={`btn btn-sm ${showAudit ? 'btn-primary' : 'btn-outline-primary'}`}
                    onClick={() => setShowAudit((v) => !v)}
                  >
                    <i className="fa fa-history me-1" aria-hidden="true" />
                    Audit History
                  </button>
                }
                emptyMessage={!providerId ? 'Select a provider.' : status.isLoading ? 'Loading…' : 'No periods.'}
              />
            </div>
          </div>

          {showAudit && (
            <div className="card custom-card mb-0">
              <div className="card-header">
                <h6 className="card-title mb-0">Audit History</h6>
              </div>
              <div className="card-body p-0">
                <DataGrid
                  columns={auditColumns}
                  data={audit.data ?? []}
                  rowKey={(r) => `${r.periodMonth}-${r.createdAt}-${r.action}`}
                  columnVisibilityToggle={false}
                  emptyMessage={audit.isLoading ? 'Loading…' : 'No audit history.'}
                />
              </div>
            </div>
          )}
      </>

      <CommentConfirmDialog
        show={confirm !== null}
        title={confirm?.title ?? ''}
        body={confirm?.body ?? ''}
        confirmLabel={confirm?.confirmLabel ?? 'Confirm'}
        confirmVariant="primary"
        busy={acting}
        onConfirm={runConfirm}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}
